import React, { FC } from 'react';
import { View } from 'react-native';
import { DailyDaypartForecast, TWCAccessibleContent } from '@components';

import { dailyWeatherPageTestIDs } from '@constants';
import { useContextNodeHandle } from '@hooks';
import { DailyForecastData } from '@store';

import { styles } from './styles';

const isPopulatedArray = (value: unknown) => Array.isArray(value) && value.length > 0;

type DailyWeatherMiddleRowProps = {
  data: DailyForecastData;
  index: number;
  isLoading: boolean;
  nextFocusDown: unknown;
  nextFocusUp: unknown;
  defaultFocus: unknown;
  error: unknown;
};

export const DailyWeatherMiddleRow: FC<DailyWeatherMiddleRowProps> = ({
  data,
  error,
  isLoading,
  index,
  nextFocusUp,
  nextFocusDown,
  defaultFocus,
}) => {
  /**
   * Check whether to display "Today" or "Tonight" data when "Today" tile is selected based on API response
   * If every element in daypart[0] is null, then assume it's past 3PM and to display "Tonight"
   */
  const isPast3PM = isPopulatedArray(data?.daypart)
    ? Object.keys(data?.daypart[0])
        .map(key => data?.daypart[0][key][0])
        .every(element => !element)
    : false;

  /**
   * Check 1: is "Today" selected and is it before 3PM?
   * Check 2: is "Today" selected and it's past 3PM?
   * Check 3: is any non "Today" tiled selected?
   * E.g. if the third tile is selected (index 2), daypart values for day are indexed at 4
   */
  const dayPartIndex = index === 0 && !isPast3PM ? index : index === 0 ? index + 1 : index + index;

  /**
   * E.g. if the third title is selected (index 2), daypart values for night are indexed at 5
   */
  const nightPartIndex = index === 0 && !isPast3PM ? index + 1 : index === 0 ? index + 2 : index + index + 1;

  const daypart = isPopulatedArray(data?.daypart) ? data.daypart[0] : undefined;
  const dayName = isPopulatedArray(daypart?.daypartName) ? daypart?.daypartName[dayPartIndex] : undefined;
  const icon = isPopulatedArray(daypart?.iconCodeExtend) ? daypart?.iconCodeExtend[dayPartIndex] : undefined;
  const dayNarrative = isPopulatedArray(daypart?.narrative) ? daypart?.narrative[dayPartIndex] : undefined;
  const dayTemperature = isPopulatedArray(daypart?.temperature) ? daypart?.temperature[dayPartIndex] : undefined;
  const sunrise = isPopulatedArray(data?.sunriseTimeUtc) ? data.sunriseTimeUtc[index] : undefined;
  const sunset = isPopulatedArray(data?.sunsetTimeUtc) ? data.sunsetTimeUtc[index] : undefined;
  const nightName = isPopulatedArray(daypart?.daypartName) ? daypart?.daypartName[nightPartIndex] : undefined;
  const nightIcon = isPopulatedArray(daypart?.iconCodeExtend) ? daypart?.iconCodeExtend[nightPartIndex] : undefined;
  const moonPhase = isPopulatedArray(data?.moonPhase) ? data?.moonPhase[index] : undefined;
  const moonPhaseCode = isPopulatedArray(data?.moonPhaseCode) ? data?.moonPhaseCode[index] : undefined;
  const nightNarrative = isPopulatedArray(daypart?.narrative) ? daypart?.narrative[nightPartIndex] : undefined;
  const nightTemperature = isPopulatedArray(daypart?.temperature) ? daypart?.temperature[nightPartIndex] : undefined;

  const middleRowLeftHandle = useContextNodeHandle(dailyWeatherPageTestIDs.dailyWeatherMiddleRowToday);
  const middleRowRightHandle = useContextNodeHandle(dailyWeatherPageTestIDs.dailyWeatherMiddleRowTomorrow);

  return (
    <View style={styles.middleRowWrapper}>
      <View style={styles.rowWrapper}>
        <TWCAccessibleContent
          disabledScreenreaderFocus={defaultFocus}
          nextFocusDown={nextFocusDown}
          nextFocusLeft={null}
          nextFocusRight={middleRowRightHandle}
          nextFocusUp={nextFocusUp}
          testID={dailyWeatherPageTestIDs.dailyWeatherMiddleRowToday}
        >
          <DailyDaypartForecast
            daypartName={dayName}
            error={error}
            iconCodeExtend={icon}
            isDaySection={isPast3PM && index === 0 ? false : true}
            isLoading={isLoading}
            moonPhase={moonPhase}
            moonPhaseCode={moonPhaseCode}
            narrative={dayNarrative}
            nextFocusDown={nextFocusDown}
            nextFocusRight={middleRowRightHandle}
            nextFocusUp={nextFocusUp}
            sunriseTimeUtc={sunrise}
            sunsetTimeUtc={sunset}
            temperature={dayTemperature}
          />
        </TWCAccessibleContent>
        <View style={styles.dayDetailDivider} />
        <TWCAccessibleContent
          disabledScreenreaderFocus={defaultFocus}
          nextFocusDown={nextFocusDown}
          nextFocusLeft={middleRowLeftHandle}
          nextFocusRight={null}
          nextFocusUp={nextFocusUp}
          testID={dailyWeatherPageTestIDs.dailyWeatherMiddleRowTomorrow}
        >
          <DailyDaypartForecast
            daypartName={nightName}
            error={error}
            iconCodeExtend={nightIcon}
            isDaySection={isPast3PM && index === 0 ? true : false}
            isLoading={isLoading}
            moonPhase={moonPhase}
            moonPhaseCode={moonPhaseCode}
            narrative={nightNarrative}
            nextFocusDown={nextFocusDown}
            nextFocusLeft={middleRowLeftHandle}
            nextFocusUp={nextFocusUp}
            sunriseTimeUtc={sunrise}
            sunsetTimeUtc={sunset}
            temperature={nightTemperature}
          />
        </TWCAccessibleContent>
      </View>
    </View>
  );
};
